<template>
  <Dialog
    :model-value="visible"
    title="Send Document"
    dialog-type="primary"
    dialog-size="small"
    :close-on-overlay="false"
    @update:model-value="(v) => { if (!v) closeDialog() }"
    @close="closeDialog"
  >
    <div class="dialog-content">
      <!-- Upload Files Section -->
      <div class="upload-section">
        <h3 class="section-title">Upload files</h3>
        <p class="section-subtitle">Max file size is XXXkb. Supported file types are PDF, docx, or jpg.</p>
        
        <!-- Upload Area -->
        <div 
          class="upload-area"
          :class="{ 'drag-over': isDragOver }"
          @drop.prevent="handleDrop"
          @dragover.prevent="handleDragOver"
          @dragenter.prevent="handleDragEnter"
          @dragleave.prevent="handleDragLeave"
        >
          <div class="upload-content">
            <div class="upload-icon">
              <img src="@/assets/MyPatient/Add_File.svg">
            </div>
            <p class="upload-text">Drag and drop your files here to add.</p>
            <p class="upload-or">OR</p>
            <button class="add-file-btn" @click="selectFiles">
              Add File
            </button>
            <input
              ref="fileInput"
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg"
              style="display: none"
              @change="handleFileSelect"
            />
          </div>
        </div>

        <!-- Uploaded Files List -->
        <div v-if="uploadedFiles.length > 0" class="uploaded-files-list">
          <div 
            v-for="(file, index) in uploadedFiles" 
            :key="index"
            class="uploaded-file-item"
          >
            <div class="file-info">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.667 1.667H5C4.08 1.667 3.342 2.417 3.342 3.333L3.333 16.667C3.333 17.583 4.075 18.333 4.992 18.333H15C15.917 18.333 16.667 17.583 16.667 16.667V6.667L11.667 1.667ZM13.333 15H6.667V13.333H13.333V15ZM13.333 11.667H6.667V10H13.333V11.667ZM10.833 7.5V2.917L15.417 7.5H10.833Z" fill="#666666"/>
              </svg>
              <div class="file-details">
                <div class="file-name">{{ file.name }}</div>
                <div class="file-meta">{{ formatFileSize(file.size) }}</div>
              </div>
            </div>
            <button class="remove-file-btn" @click="removeFile(index)">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4L4 12M4 4L12 12" stroke="#999999" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <ResponsiveButton button_type="outlined" built_in_theme="error" size="medium" @click="closeDialog">Cancel</ResponsiveButton>
      <ResponsiveButton button_type="filled" built_in_theme="dark" size="medium" :disabled="uploadedFiles.length === 0" @click="uploadFiles">Upload File</ResponsiveButton>
    </template>
  </Dialog>
</template>

<script>
export default {
  name: 'SendDocumentDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'upload'],
  data() {
    return {
      uploadedFiles: [],
      isDragOver: false
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      }
    }
  },
  methods: {
    closeDialog() {
      this.uploadedFiles = [];
      this.$emit('close');
    },
    selectFiles() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.addFiles(files);
      event.target.value = '';
    },
    handleDrop(event) {
      this.isDragOver = false;
      const files = Array.from(event.dataTransfer.files);
      this.addFiles(files);
    },
    handleDragOver(event) {
      this.isDragOver = true;
    },
    handleDragEnter(event) {
      this.isDragOver = true;
    },
    handleDragLeave(event) {
      this.isDragOver = false;
    },
    addFiles(files) {
      const validFiles = files.filter(file => this.isValidFile(file));
      this.uploadedFiles.push(...validFiles);
      
      const invalidFiles = files.filter(file => !this.isValidFile(file));
      if (invalidFiles.length > 0) {
        this.$message.warning(`Some files were not added. Only PDF, DOCX, and JPG files are supported.`);
      }
    },
    isValidFile(file) {
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg'
      ];
      return allowedTypes.includes(file.type);
    },
    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    uploadFiles() {
      if (this.uploadedFiles.length === 0) return;
      
      this.$emit('upload', this.uploadedFiles);
      this.uploadedFiles = [];
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.dialog-content {
  padding: 24px;
  background: #F9FBFD;
}

.upload-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-title {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #333333;
  margin: 0;
}

.section-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #979797;
  margin: 0 0 16px 0;
}

/* Upload Area */
.upload-area {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  background: #F2F2F2;
  border: 2px dashed #C4C4C4;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.upload-area.drag-over {
  border-color: #004879;
  background: #F0F7FF;
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px;
}

.upload-icon {
  margin-bottom: 8px;
  padding: 8px;
  background: #e0e0e0;
  border-radius: 5px;
}

.upload-text {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #979797;
  margin: 0;
}

.upload-or {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #979797;
  margin: 0;
}

.add-file-btn {
  padding: 4px 32px;
  background: #004879;
  border: none;
  border-radius: 15px;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #FFFFFF;
  cursor: pointer;
  transition: background 0.3s ease;
  box-shadow: 0px 1px 3px 0px rgba(74, 74, 74, 0.25);
}

.add-file-btn:hover {
  background: #003366;
}

/* Uploaded Files List */
.uploaded-files-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
}

.uploaded-file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.file-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.file-name {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #333333;
}

.file-meta {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #999999;
}

.remove-file-btn {
  width: 24px;
  height: 24px;
  padding: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.remove-file-btn:hover {
  background: #F3F4F6;
}

</style>